import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import { concatMap, map, shareReplay, tap, toArray } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { ScopeService } from './scope.service';


export interface ReferenceFieldDataTypeValueLabel {
  value: KPI.ReferenceFieldDataType;
  label: string;
}

export interface ReferenceFieldFilterTypeValueLabel {
  value: KPI.ReferenceFieldFilterType;
  label: string;
}

@Injectable()
export class ReferenceFieldService {

  private _referenceFieldDataTypes$: Observable<ReferenceFieldDataTypeValueLabel[]>;
  private _referenceFieldFilterTypes$: Observable<ReferenceFieldFilterTypeValueLabel[]>;

  constructor(
    private api: APIService,
    private scope: ScopeService,
    private translate: TranslateService,
  ) { }

  /**
   * Recover all data type for a reference field
   */
  public get referenceFieldDataTypes$(): Observable<ReferenceFieldDataTypeValueLabel[]> {
    if (!this._referenceFieldDataTypes$) {
      this._referenceFieldDataTypes$ = from(
        [
          {
            value: KPI.ReferenceFieldDataType.DATA_TYPE_FK,
            label: _('core.utils.reference_field.type_fk'),
          },
          {
            value: KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER,
            label: _('core.utils.reference_field.type_number'),
          },
          {
            value: KPI.ReferenceFieldDataType.DATA_TYPE_STRING,
            label: _('core.utils.reference_field.type_string'),
          },
          {
            value: KPI.ReferenceFieldDataType.DATA_TYPE_USER,
            label: _('core.utils.reference_field.type_user'),
          },
        ],
      ).pipe(
        concatMap((item) =>
          this.translate.get(item.label).pipe(
            map((translatedLabel) => ({
              ...item,
              label: translatedLabel,
            })),
          ),
        ),
        toArray(),
        shareReplay(1),
      );
    }

    return this._referenceFieldDataTypes$;
  }


  /**
   * Recover all filter type for a reference field
   */
  public get referenceFieldDataFilter$(): Observable<ReferenceFieldFilterTypeValueLabel[]> {
    if (!this._referenceFieldFilterTypes$) {
      this._referenceFieldFilterTypes$ = from(
        [
          {
            value: KPI.ReferenceFieldFilterType.FILTER_NO,
            label: _('core.utils.reference_field.filter_no'),
          },
          {
            value: KPI.ReferenceFieldFilterType.FILTER_RANGE,
            label: _('core.utils.reference_field.filter_range'),
          },
          {
            value: KPI.ReferenceFieldFilterType.FILTER_VALUE,
            label: _('core.utils.reference_field.filter_value'),
          },
        ],
      ).pipe(
        concatMap((item) =>
          this.translate.get(item.label).pipe(
            map((translatedLabel) => ({
              ...item,
              label: translatedLabel,
            })),
          ),
        ),
        toArray(),
        shareReplay(1),
      );
    }

    return this._referenceFieldFilterTypes$;
  }

  /**
   * Calls the api to update a reference field
   *
   * @param idRefField
   * @param referenceField
   */
  public updateReferenceField(
    idRefField: number, referenceField: Partial<KPI.ReferenceField>,
  ): Observable<KPI.ReferenceField> {
    return this.api.reference_data_reference_fields_patch(idRefField, referenceField).pipe(
      tap(() => this.scope.refreshReferenceFields()),
    );
  }

  /**
   * Calls the api to create a reference field
   *
   * @param referenceField
   */
  public createReferenceField(referenceField: Partial<KPI.ReferenceField>): Observable<KPI.ReferenceField> {
    return this.api.reference_data_reference_fields_post(referenceField).pipe(
      tap(() => this.scope.refreshReferenceFields()),
    );
  }
}
